import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Check, X, Loader2, Upload, ShieldCheck, ClipboardCheck, PhoneCall, BadgeCheck } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

export const CP_BENEFITS = [
    "Quality property leads", "Access to verified projects", "Builder-direct opportunities", "Project training",
    "Marketing support", "Sales support", "CRM / support system", "Commission opportunities",
    "Multiple project options across India", "Dedicated relationship support",
];

export const PAID_PENDING_MESSAGE = "Thank you for registering as an EBG Realty Premium Channel Partner. Your application is under review. Our team will contact you regarding eligibility, payment and membership confirmation.";

const COMPARISON = [
    ["Channel Partner Registration", "Yes", "Yes"],
    ["Property Upload", "Yes", "Yes"],
    ["Additional Training", "No", "Yes"],
    ["EBG Brands Access", "No", "Yes"],
    ["Membership Approval", "Standard registration / approval", "Subject to EBG eligibility & confirmation"],
    ["Membership Fee", "None", "EBG team will contact Partner separately"],
];

const Cell = ({ v }) => (v === "Yes" ? <Check size={16} className="text-emerald-600 mx-auto"/> : v === "No" ? <X size={16} className="text-black/30 mx-auto"/> : <span>{v}</span>);

export function PartnerTypeCards() {
    return (
        <div className="grid md:grid-cols-2 gap-5" data-testid="cp-type-cards">
            <div className="bg-white border border-black/10 p-7 flex flex-col" data-testid="cp-type-free">
                <div className="flex items-center gap-3"><span className="w-11 h-11 rounded-full border border-[var(--ebg-gold)] text-[var(--ebg-gold)] flex items-center justify-center"><Upload size={18}/></span><div className="text-[10px] uppercase tracking-[0.26em] font-bold text-[var(--ebg-gold)]">Channel Partner</div></div>
                <h3 className="font-serif text-3xl mt-4">Channel Partner</h3>
                <p className="text-black/60 mt-3 leading-relaxed flex-1">Upload and manage your properties through EBG Realty.</p>
                <Link to="/partner-registration?type=partner" data-testid="cp-register-free" className="mt-6 bg-black text-white py-3.5 text-[11px] uppercase tracking-[0.22em] font-bold text-center hover:bg-[var(--ebg-gold)] hover:text-black transition-colors inline-flex items-center justify-center gap-2">Register as Channel Partner <ArrowRight size={12}/></Link>
            </div>
            <div className="bg-[#0a0d18] text-white border border-[var(--ebg-gold)]/40 p-7 flex flex-col" data-testid="cp-type-paid">
                <div className="flex items-center gap-3"><span className="w-11 h-11 rounded-full bg-[var(--ebg-gold)] text-black flex items-center justify-center"><BadgeCheck size={18}/></span><div className="text-[10px] uppercase tracking-[0.26em] font-bold text-[var(--ebg-gold)]">Premium</div></div>
                <h3 className="font-serif text-3xl mt-4">Premium Channel Partner</h3>
                <p className="text-white/70 mt-3 leading-relaxed flex-1">Access additional training, EBG Brands and enhanced Partner opportunities, subject to eligibility and confirmation by EBG Realty.</p>
                <Link to="/partner-registration?type=premium" data-testid="cp-register-paid" className="mt-6 bg-[var(--ebg-gold)] text-black py-3.5 text-[11px] uppercase tracking-[0.22em] font-bold text-center hover:bg-white transition-colors inline-flex items-center justify-center gap-2">Register as Premium Partner <ArrowRight size={12}/></Link>
            </div>
        </div>
    );
}

export function PartnerComparison() {
    return (
        <div className="overflow-x-auto border border-black/10 bg-white" data-testid="cp-comparison">
            <table className="w-full table-fixed text-[12px] sm:text-[13.5px]">
                <thead><tr className="bg-black text-white text-[10px] uppercase tracking-[0.2em]">
                    <th className="text-left p-3 sm:p-4 w-[36%]">Feature</th><th className="p-3 sm:p-4 w-[30%]">Channel Partner</th><th className="p-3 sm:p-4 w-[34%] text-[var(--ebg-gold)]">Premium Channel Partner</th>
                </tr></thead>
                <tbody>
                    {COMPARISON.map(([f, a, b]) => (
                        <tr key={f} className="border-t border-black/8">
                            <td className="p-3 sm:p-4 font-bold break-words">{f}</td>
                            <td className="p-3 sm:p-4 text-center text-black/70 break-words"><Cell v={a}/></td>
                            <td className="p-3 sm:p-4 text-center text-black/70 break-words"><Cell v={b}/></td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

const STEPS = [
    { Icon: ClipboardCheck, t: "Choose & register", d: "Pick Channel Partner or Premium Channel Partner and submit your registration." },
    { Icon: ShieldCheck, t: "EBG review", d: "Our partner-relations team reviews every application." },
    { Icon: PhoneCall, t: "Premium: eligibility & confirmation", d: "For Premium Channel Partners, our team contacts you about eligibility, payment and membership confirmation. No payment is collected online." },
    { Icon: BadgeCheck, t: "Get access", d: "Once approved you receive your login details for the Partner portal." },
];

export function PartnerProgrammeLanding() {
    return (
        <div data-testid="cp-programme">
            <header className="text-center mb-12">
                <div className="text-[10px] md:text-[11px] uppercase tracking-[0.28em] font-bold text-[var(--ebg-gold)]">EBG Realty Channel Partner Programme</div>
                <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-3 leading-[1.05]">Become an EBG Realty<br/><em className="not-italic text-[var(--ebg-gold)]">Channel Partner.</em></h1>
                <p className="text-black/60 mt-5 max-w-2xl mx-auto text-sm md:text-base">Your Network + Our Platform = Bigger Business. Choose the partnership that suits you.</p>
            </header>
            <PartnerTypeCards/>
            <section className="mt-16">
                <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-[var(--ebg-gold)]">Programme Benefits</div>
                <h2 className="font-serif text-3xl md:text-4xl mt-2">Why partner with EBG Realty</h2>
                <ul className="mt-6 grid sm:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="cp-programme-benefits">
                    {CP_BENEFITS.map((b) => <li key={b} className="bg-white border border-black/8 p-4 text-[13.5px] flex items-start gap-2"><Check size={14} className="text-[var(--ebg-gold)] mt-0.5 shrink-0"/>{b}</li>)}
                </ul>
            </section>
            <section className="mt-16">
                <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-[var(--ebg-gold)]">Channel Partner vs Premium</div>
                <h2 className="font-serif text-3xl md:text-4xl mt-2 mb-6">Compare partnerships</h2>
                <PartnerComparison/>
            </section>
            <section className="mt-16" data-testid="cp-process">
                <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-[var(--ebg-gold)]">Registration Process</div>
                <h2 className="font-serif text-3xl md:text-4xl mt-2">How it works</h2>
                <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {STEPS.map(({ Icon, t, d }, i) => (
                        <div key={t} className="bg-white border border-black/8 p-5">
                            <div className="flex items-center gap-2 text-[var(--ebg-gold)]"><Icon size={16}/><span className="font-serif text-xl">0{i + 1}</span></div>
                            <div className="font-bold mt-3">{t}</div>
                            <p className="text-black/60 text-[13px] mt-1.5 leading-relaxed">{d}</p>
                        </div>
                    ))}
                </div>
                <p className="mt-6 text-[13px] text-black/60 border-l-4 border-[var(--ebg-gold)] pl-4" data-testid="cp-paid-eligibility-note">
                    <b>Premium Channel Partner eligibility:</b> Premium membership is subject to EBG Realty&rsquo;s eligibility review and confirmation. No payment is collected on this website &mdash; our team will contact you directly. Membership is valid for one year from the date EBG Realty activates it.
                </p>
            </section>
        </div>
    );
}

const inputCls = "w-full border border-black/15 bg-white px-3.5 py-3 text-sm outline-none focus:border-[var(--ebg-gold)]";
const FREE_EMPTY = { proprietor_name: "", email: "", mobile: "", pin_code: "", city: "", office_address: "" };
const FREE_FIELDS = [
    { k: "proprietor_name", l: "Name*", type: "text" },
    { k: "email", l: "Email ID (optional)", type: "email" },
    { k: "mobile", l: "Phone Number*", type: "tel" },
    { k: "pin_code", l: "PIN Code (optional)", type: "text" },
    { k: "city", l: "City*", type: "text" },
];

const validateFree = (f) => {
    const e = {};
    if (f.proprietor_name.trim().length < 2) e.proprietor_name = "Please enter your name.";
    if (f.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(f.email.trim())) e.email = "Enter a valid email address.";
    if (f.mobile.replace(/\D/g, "").length < 10 || !/^\+?[0-9][0-9\s-]{9,16}$/.test(f.mobile.trim())) e.mobile = "Enter a valid phone number.";
    if (f.pin_code.trim() && !/^[0-9]{6}$/.test(f.pin_code.trim())) e.pin_code = "Enter a 6-digit PIN code.";
    if (f.city.trim().length < 2) e.city = "Please enter your city.";
    if (f.office_address.trim().length < 5) e.office_address = "Please enter your address.";
    return e;
};

export function FreePartnerRegistration({ onDone }) {
    const [f, setF] = useState(FREE_EMPTY);
    const [errors, setErrors] = useState({});
    const [busy, setBusy] = useState(false);
    const upd = (k, v) => { setF((p) => ({ ...p, [k]: v })); if (errors[k]) setErrors((e) => ({ ...e, [k]: undefined })); };

    const submit = async (ev) => {
        ev.preventDefault();
        if (busy) return;
        const e = validateFree(f);
        setErrors(e);
        if (Object.values(e).some(Boolean)) return;
        setBusy(true);
        try {
            const { data } = await api.post("/cp-applications/free", Object.fromEntries(Object.entries(f).map(([k, v]) => [k, v.trim()])));
            onDone({ ...data, email: f.email.trim(), mobile: f.mobile.trim() });
        } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
    };
    const err = (k) => errors[k] && <div className="text-[11px] text-red-600 mt-1" data-testid={`cp-free-error-${k}`}>{errors[k]}</div>;

    return (
        <div className="max-w-2xl mx-auto" data-testid="cp-free-registration">
            <header className="text-center mb-8">
                <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-[var(--ebg-gold)]">Channel Partner Registration</div>
                <h1 className="font-serif text-4xl md:text-5xl mt-3 leading-[1.05]">Upload &amp; manage your properties</h1>
                <p className="text-black/60 mt-4 text-sm">A quick registration. Our team reviews it and shares your Partner portal login details. Email and PIN code are optional.</p>
            </header>
            <form onSubmit={submit} noValidate className="bg-white border border-black/8 p-6 md:p-10 grid md:grid-cols-2 gap-5">
                {FREE_FIELDS.map((fd) => (
                    <label key={fd.k} className={`block ${fd.k === "proprietor_name" ? "md:col-span-2" : ""}`}>
                        <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-black/55 mb-1.5">{fd.l}</div>
                        <input type={fd.type} value={f[fd.k]} onChange={(e) => upd(fd.k, e.target.value)} className={inputCls} data-testid={`cp-free-${fd.k}`}/>
                        {err(fd.k)}
                    </label>
                ))}
                <label className="block md:col-span-2">
                    <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-black/55 mb-1.5">Address*</div>
                    <textarea rows={2} value={f.office_address} onChange={(e) => upd("office_address", e.target.value)} className={inputCls} data-testid="cp-free-office_address"/>
                    {err("office_address")}
                </label>
                <div className="md:col-span-2 flex flex-wrap items-center justify-between gap-3 pt-2">
                    <Link to="/partner-registration" data-testid="cp-free-back" className="text-[11px] uppercase tracking-[0.22em] font-bold text-black/50 hover:text-black">&larr; Change partner type</Link>
                    <button type="submit" disabled={busy} data-testid="cp-free-submit" className="bg-[var(--ebg-gold)] text-black px-8 py-3.5 text-[11px] uppercase tracking-[0.22em] font-bold hover:bg-black hover:text-[var(--ebg-gold)] transition-colors disabled:opacity-60 inline-flex items-center gap-2">
                        {busy && <Loader2 size={13} className="animate-spin"/>} Submit Registration
                    </button>
                </div>
            </form>
        </div>
    );
}
